// Synthesized sound effects via Web Audio API.
// Avoids shipping audio assets and keeps the bundle tiny.
// Real samples can be swapped in later by replacing the body of each `play*` function.

let ctx: AudioContext | null = null;
let muted = false;

function getCtx(): AudioContext | null {
  if (muted) return null;
  if (ctx) return ctx;
  try {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}

export function setMuted(value: boolean): void {
  muted = value;
  if (muted && ctx) {
    void ctx.suspend();
  } else if (!muted && ctx && ctx.state === "suspended") {
    void ctx.resume();
  }
}

// Resume on first user gesture (browsers require this).
let unlocked = false;
export function unlockOnFirstGesture(): void {
  if (unlocked) return;
  const handler = () => {
    unlocked = true;
    const c = getCtx();
    if (c && c.state === "suspended") void c.resume();
    window.removeEventListener("pointerdown", handler);
    window.removeEventListener("keydown", handler);
  };
  window.addEventListener("pointerdown", handler, { once: true });
  window.addEventListener("keydown", handler, { once: true });
}

// Generic envelope: brief percussive sound with attack/decay.
function blip(opts: {
  freq: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
  sweepTo?: number;
}): void {
  const c = getCtx();
  if (!c) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = opts.type ?? "sine";
  osc.frequency.setValueAtTime(opts.freq, t);
  if (opts.sweepTo !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(20, opts.sweepTo),
      t + opts.duration,
    );
  }
  const peak = opts.gain ?? 0.18;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(peak, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + opts.duration);
  osc.connect(g).connect(c.destination);
  osc.start(t);
  osc.stop(t + opts.duration + 0.02);
}

// Short noise burst for paper-like card draw.
function noiseBurst(opts: { duration: number; gain?: number; lpFreq?: number }): void {
  const c = getCtx();
  if (!c) return;
  const t = c.currentTime;
  const bufferSize = Math.max(1, Math.floor(c.sampleRate * opts.duration));
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const src = c.createBufferSource();
  src.buffer = buffer;
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = opts.lpFreq ?? 2200;
  const g = c.createGain();
  g.gain.value = opts.gain ?? 0.12;
  src.connect(lp).connect(g).connect(c.destination);
  src.start(t);
  src.stop(t + opts.duration);
}

// ===== Public sound API =====

export function playPlace(): void {
  blip({ freq: 320, duration: 0.08, type: "triangle", gain: 0.16, sweepTo: 220 });
}

export function playFlip(): void {
  noiseBurst({ duration: 0.12, gain: 0.18, lpFreq: 3500 });
}

export function playDraw(): void {
  noiseBurst({ duration: 0.07, gain: 0.14, lpFreq: 2400 });
  blip({ freq: 540, duration: 0.05, type: "sine", gain: 0.06 });
}

export function playFoundation(): void {
  // Bright two-note chirp signaling progress.
  blip({ freq: 660, duration: 0.07, type: "sine", gain: 0.12 });
  setTimeout(() => blip({ freq: 880, duration: 0.1, type: "sine", gain: 0.1 }), 50);
}

export function playInvalid(): void {
  blip({ freq: 200, duration: 0.12, type: "square", gain: 0.08, sweepTo: 130 });
}

export function playUndo(): void {
  blip({ freq: 420, duration: 0.08, type: "triangle", gain: 0.12, sweepTo: 540 });
}

export function playWin(): void {
  // Mini fanfare: arpeggio C-E-G-C
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((f, i) => {
    setTimeout(
      () => blip({ freq: f, duration: 0.22, type: "triangle", gain: 0.18 }),
      i * 110,
    );
  });
}
